import React, { useState, useEffect } from 'react';
import { Modal, Text, Button, TextField, Select, AutoComplete } from 'porosys-ui';
import { Tabs, Tab, CircularProgress } from '@material-ui/core';
import { getDepartments } from '../../../common/services/api/CommonApis/Department';
import { syncCompareUsers, syncImportUsers, syncDeleteUsers } from '../api';
import DeleteConfirmation from '../../../common/ui/components/DeleteConfirmation/DeleteConfirmation';
import './styles.scss';

const TAB_MISSING_IN_MEETINGS = 0;
const TAB_MISSING_IN_POROSYS = 1;

const isImportRowValid = row => row.password && row.departments && row.departments.length > 0;

const UserSyncModal = ({ isOpen, onClose, onSyncComplete }) => {
    const [activeTab, setActiveTab] = useState(TAB_MISSING_IN_MEETINGS);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [importRows, setImportRows] = useState([]);
    const [deleteRows, setDeleteRows] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!isOpen) return;

        const loadCompareData = async () => {
            setLoading(true);
            setErrorMessage('');

            const departmentsList = await getDepartments();
            setDepartments(departmentsList || []);

            const result = await syncCompareUsers();

            if (!result.success) {
                setErrorMessage(result.message);
                setImportRows([]);
                setDeleteRows([]);
                setLoading(false);
                return;
            }

            setImportRows(
                (result.data.missingInMeetings || []).map(user => ({
                    key: user.email,
                    fullname: user.fullName,
                    email: user.email,
                    anotherEmail: user.anotherEmail || user.email,
                    password: '',
                    role: 'user',
                    departments: [],
                    selected: true,
                }))
            );

            setDeleteRows(
                (result.data.missingInPorosys || []).map(user => ({
                    key: user._id,
                    _id: user._id,
                    fullname: user.fullname,
                    email: user.email,
                    anotherEmail: user.anotherEmail,
                    role: user.role,
                    selected: true,
                }))
            );

            setLoading(false);
        };

        loadCompareData();
    }, [isOpen]);

    const handleImportRowChange = (key, field, value) => {
        setImportRows(rows =>
            rows.map(row => {
                if (row.key !== key) return row;
                const updated = { ...row, [field]: value };
                if (isImportRowValid(updated)) {
                    updated.selected = true;
                }
                return updated;
            })
        );
    };

    const toggleImportRow = key => {
        setImportRows(rows =>
            rows.map(row => {
                if (row.key !== key || !isImportRowValid(row)) return row;
                return { ...row, selected: !row.selected };
            })
        );
    };

    const toggleDeleteRow = key => {
        setDeleteRows(rows => rows.map(row => (row.key === key ? { ...row, selected: !row.selected } : row)));
    };

    const toggleAllImport = checked => {
        setImportRows(rows =>
            rows.map(row => ({
                ...row,
                selected: checked && isImportRowValid(row),
            }))
        );
    };

    const toggleAllDelete = checked => {
        setDeleteRows(rows => rows.map(row => ({ ...row, selected: checked })));
    };

    const selectedImportCount = importRows.filter(row => row.selected && isImportRowValid(row)).length;
    const selectedDeleteCount = deleteRows.filter(row => row.selected).length;

    const handleImport = async () => {
        const usersToImport = importRows
            .filter(row => row.selected && isImportRowValid(row))
            .map(row => ({
                fullname: row.fullname,
                email: row.email,
                anotherEmail: row.anotherEmail,
                password: row.password,
                role: row.role,
                departments: row.departments.map(department => department._id),
            }));

        if (!usersToImport.length) return;

        setIsSubmitting(true);
        const result = await syncImportUsers(usersToImport);
        setIsSubmitting(false);

        if (result.success) {
            onSyncComplete();
            onClose();
        }
    };

    const handleDelete = async () => {
        const userIds = deleteRows.filter(row => row.selected).map(row => row._id);

        if (!userIds.length) return;

        setIsSubmitting(true);
        const result = await syncDeleteUsers(userIds);
        setIsSubmitting(false);
        setIsDeleteConfirmationOpen(false);

        if (result.success) {
            onSyncComplete();
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <Modal
                classes={{ root: 'user-sync-modal' }}
                title="همگام‌سازی کاربران با پروسیس"
                onClose={onClose}
                open={true}
            >
                <div className="user-sync-content">
                    <Tabs value={activeTab} onChange={(e, value) => setActiveTab(value)} indicatorColor="primary">
                        <Tab label={`فقط در پروسیس (${importRows.length})`} />
                        <Tab label={`فقط در جلسات (${deleteRows.length})`} />
                    </Tabs>

                    {loading ? (
                        <div className="user-sync-loading">
                            <CircularProgress />
                        </div>
                    ) : errorMessage ? (
                        <Text classes={{ root: 'user-sync-error' }}>{errorMessage}</Text>
                    ) : (
                        <>
                            {activeTab === TAB_MISSING_IN_MEETINGS && (
                                <div className="user-sync-tab">
                                    <table className="user-sync-table">
                                        <thead>
                                            <tr>
                                                <th>
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            importRows.length > 0 &&
                                                            importRows.every(
                                                                row => !isImportRowValid(row) || row.selected
                                                            )
                                                        }
                                                        onChange={e => toggleAllImport(e.target.checked)}
                                                    />
                                                </th>
                                                <th>نام</th>
                                                <th>ایمیل</th>
                                                <th>ایمیل دوم</th>
                                                <th>رمز عبور</th>
                                                <th>نقش</th>
                                                <th>واحدها</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {importRows.length ? (
                                                importRows.map(row => (
                                                    <tr key={row.key}>
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                checked={row.selected}
                                                                disabled={!isImportRowValid(row)}
                                                                onChange={() => toggleImportRow(row.key)}
                                                            />
                                                        </td>
                                                        <td>{row.fullname}</td>
                                                        <td>{row.email}</td>
                                                        <td>{row.anotherEmail}</td>
                                                        <td>
                                                            <TextField
                                                                classes={{ root: 'sync-inline-input' }}
                                                                value={row.password}
                                                                type="password"
                                                                placeholder="رمز عبور"
                                                                onChange={e =>
                                                                    handleImportRowChange(
                                                                        row.key,
                                                                        'password',
                                                                        e.target.value
                                                                    )
                                                                }
                                                            />
                                                        </td>
                                                        <td>
                                                            <Select
                                                                selected={row.role}
                                                                classes={{ root: 'sync-inline-input' }}
                                                                onChange={e =>
                                                                    handleImportRowChange(
                                                                        row.key,
                                                                        'role',
                                                                        e.target.value
                                                                    )
                                                                }
                                                                options={[
                                                                    { label: 'ادمین', value: 'admin' },
                                                                    { label: 'اپراتور', value: 'operator' },
                                                                    { label: 'کاربر', value: 'user' },
                                                                ]}
                                                            />
                                                        </td>
                                                        <td>
                                                            <AutoComplete
                                                                selected={row.departments}
                                                                classes={{ input: 'sync-inline-input' }}
                                                                multiple
                                                                onChange={(e, newValue) =>
                                                                    handleImportRowChange(
                                                                        row.key,
                                                                        'departments',
                                                                        newValue
                                                                    )
                                                                }
                                                                getOptionSelected={(option, value) =>
                                                                    option._id === value._id
                                                                }
                                                                getOptionLabel={option =>
                                                                    (option && 'label' in option && option.label) || ''
                                                                }
                                                                options={departments}
                                                                placeholder="انتخاب واحد"
                                                            />
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={7}>کاربری برای افزودن وجود ندارد</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                    <Button
                                        color="primary"
                                        disabled={!selectedImportCount || isSubmitting}
                                        onClick={handleImport}
                                    >
                                        افزودن انتخاب‌شده‌ها ({selectedImportCount})
                                    </Button>
                                </div>
                            )}

                            {activeTab === TAB_MISSING_IN_POROSYS && (
                                <div className="user-sync-tab">
                                    <table className="user-sync-table">
                                        <thead>
                                            <tr>
                                                <th>
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            deleteRows.length > 0 &&
                                                            deleteRows.every(row => row.selected)
                                                        }
                                                        onChange={e => toggleAllDelete(e.target.checked)}
                                                    />
                                                </th>
                                                <th>نام</th>
                                                <th>ایمیل</th>
                                                <th>نقش</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {deleteRows.length ? (
                                                deleteRows.map(row => (
                                                    <tr key={row.key}>
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                checked={row.selected}
                                                                onChange={() => toggleDeleteRow(row.key)}
                                                            />
                                                        </td>
                                                        <td>{row.fullname}</td>
                                                        <td>{row.email}</td>
                                                        <td>{row.role}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={4}>کاربری برای حذف وجود ندارد</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                    <Button
                                        color="primary"
                                        disabled={!selectedDeleteCount || isSubmitting}
                                        onClick={() => setIsDeleteConfirmationOpen(true)}
                                    >
                                        حذف انتخاب‌شده‌ها ({selectedDeleteCount})
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Modal>

            <DeleteConfirmation
                isOpen={isDeleteConfirmationOpen}
                onClose={() => setIsDeleteConfirmationOpen(false)}
                onClickOk={handleDelete}
            />
        </>
    );
};

export default UserSyncModal;
